/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { FC, useContext, useMemo } from 'react';
import clsx from 'clsx';

import { ReportAssumption, ReportPaymentType, ReportSheetsAssumption, ReportSystemCalc } from '@futrli-sdk/predict';

import { ASSUMPTION_TYPES, MAX_DIGITS_IN_CELL, PAYMENT_TYPES, SIZES } from '../../../../../../core/constants';
import { PredictionsAccountCategories, VIEW_TYPES } from '../../../../../../core/types';
import { usePredictionsInfoContext } from '../../../../../../forecast/detail/context/predictions-info-context';
import { PredictManagementContext } from '../../../../../../predict-management/context/predict-management-context';
import { Cell, CellProps } from '../../../../../../ui/data-grid/components/cell';
import { CurrentPeriodCell } from '../../../../../../ui/data-grid/components/current-period/current-period-cell';
import { isCurrentPeriod } from '../../../../../../ui/data-grid/components/current-period/current-period';
import { useDataGridLayout } from '../../../../../../ui/data-grid/components/data-grid-layout-context';
import { NOT_ALLOWED_NUMBER_EXPLAINED } from '../../../../../../ui/data-grid/components/number-explained/number-explained-messages';
import { UserGridSettingsContext } from '../../../user-grid-settings/user-grid-settings-context';
import { getColors } from '../../style-rows';
import { getFYIndexes } from '../../format-rows';
import { isAiAssumption, isInvoiceAssumption, isVatRow } from '../../headers/header-row-utils';
import { nestedAccountHeightOverride } from '../../style-rows';
import { AssumptionRow, PaymentTypeRow, SheetsAssumptionRow, SystemCalcRow } from '../../types';

import grid from '../../../../../../ui/data-grid/components/grid.scss';

export interface Props {
  row: AssumptionRow | PaymentTypeRow | SheetsAssumptionRow | SystemCalcRow;
  rowHeight: SIZES;
  showDecimals: boolean;
  isLast?: boolean;
  columns: string[];
  category?: PredictionsAccountCategories;
  viewType?: VIEW_TYPES;
}

type AssumptionOrPaymentType = ReportAssumption | ReportPaymentType | ReportSheetsAssumption | ReportSystemCalc;

export const isNumberExplainedAllowed = (assumptionOrPaymentType: AssumptionOrPaymentType) => {
  // only show TNE for non-derived futrli predicts rows and their payments
  if (
    assumptionOrPaymentType.type == PAYMENT_TYPES.AI_DAYS ||
    (assumptionOrPaymentType.type == ASSUMPTION_TYPES.AI && !assumptionOrPaymentType.is_derived)
  ) {
    return true;
  }

  return false;
};

export const RowAssumption: FC<Props> = (props: Props): JSX.Element | null => {
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { forecast, periodType, forecastGraphDate, showPredictionsExplanation } = usePredictionsInfoContext();
  const dataGridLayout = useDataGridLayout();
  const { isInEditingMode } = useContext(UserGridSettingsContext);

  const fyIndexes = useMemo(() => getFYIndexes(props.columns), [props.columns]);

  const renderCellComponent = (
    assumptionOrPayment: AssumptionOrPaymentType,
    cellValue: string,
    cellPosition: number,
    hideCellContent: boolean
  ) => {
    const { row, rowHeight, showDecimals, isLast, columns, category } = props;
    const cellDateIndex = columns[cellPosition];
    const accountSwitchId =
      (row as AssumptionRow).account.paymenttype_id || (row as PaymentTypeRow).account.assumption_id;

    const cellProps: CellProps & { key: string } = {
      category,
      periodType,
      showDecimals,
      rowHeight: nestedAccountHeightOverride(row, rowHeight, 1),
      key: cellDateIndex,
      index: cellDateIndex,
      value: cellValue,
      maxDigits: MAX_DIGITS_IN_CELL,
      isChild: true,
      isFirst: row.siblingIndex === 0,
      isLast,
      strikeThrough: (assumptionOrPayment as ReportAssumption).excluded_from_report,
      numberExplained: !isNumberExplainedAllowed(row.account)
        ? NOT_ALLOWED_NUMBER_EXPLAINED
        : {
            allowed: true,
            data: {
              accountSwitchId,
              reportSection: row.section,
              accountId: row.account.index,
              type: row.kind,
              accountName: row.account.name,
              isAiAssumption: isAiAssumption(row)
            }
          },
      isFY: fyIndexes.indexOf(cellPosition) !== -1
    };

    const currentPeriod =
      periodType && forecast?.kind !== 'budget' && isCurrentPeriod(cellDateIndex, periodType, forecastGraphDate);

    return currentPeriod ? (
      <CurrentPeriodCell
        {...cellProps}
        isAssumption={true}
        hidePeriodToDate={cellPosition === 0}
        hideCellContent={hideCellContent}
      />
    ) : (
      <Cell
        {...cellProps}
        isAssumption={true}
        calculatedDateToCheck={forecastGraphDate}
        hideCellContent={hideCellContent}
      />
    );
  };

  if (!forecast) {
    addErrorMessage?.(new Error('Missed forecast in order to build the row'));
    return null;
  }

  const { row, category, viewType } = props;
  const { id: forecastId } = forecast;

  const inEditingMode = isInEditingMode(forecastId, row);
  const categoryName = category ?? '';

  const rowClasses = clsx({
    [grid.row]: true,
    [grid.isChild]: true,
    [grid.isPrediction]: true,
    [grid.isAiAssumption]: isAiAssumption(row),
    [grid.isInvoiceAssumption]: isInvoiceAssumption(row),
    [grid.isVat]: isVatRow(row),
    [grid[categoryName]]: !!category
  });

  return (
    <div
      role="cell"
      className={rowClasses}
      style={
        !inEditingMode
          ? {
              backgroundColor: getColors(category || 'income', viewType)[2],
              ...(dataGridLayout ? { width: '100%' } : {})
            }
          : dataGridLayout
          ? { width: '100%' }
          : undefined
      }
    >
      {row.account.period_data.map((cell, index) =>
        renderCellComponent(row.account, cell, index, !!(showPredictionsExplanation && isAiAssumption(row)))
      )}
    </div>
  );
};
